/**
 * Vocabulário do guia — o cartão de estado e os deep-links da entrada
 * (spec-001 do project-onboarding-with-ai-fee-fixo, [T004]).
 *
 * As chaves de `passo`, `situacao` e `etapa` NÃO nascem aqui: são as do motor
 * do backend (`aawz_fee_fixo/core/plano_de_acao.py` e
 * `core/cobertura_carteira.py`). Este arquivo só as traduz para a tela — o
 * rótulo do selo, o nome legível da etapa e o texto do botão. Recalcular
 * qualquer situação aqui criaria uma segunda régua do método, e a régua do
 * método é do guia, não da tela.
 *
 * O `acao` do deep-link é contrato com a tela de Clientes
 * (`view-fee-fixo-clientes.vue` lê `?acao=` no `created()`) e aparece em link
 * que o consultor pode ter salvo — mudá-lo quebra atalho já em uso.
 */

import { pluralize } from '@utils/Format'

// ── Deep-link ──────────────────────────────────────────────────────────────
export const PARAM_ACAO = 'acao'
export const ACAO_IMPORTAR_CLIENTES = 'importar-clientes'
export const ACAO_IMPORTAR_ATIVOS = 'importar-ativos'
// O recorte que a IA sugeriu na conversa, aplicado na aba Clientes (spec-005,
// [T009] — US-7). O FILTRO viaja pela store (`feeFixoIa/filtroSugerido`), não
// pela URL: ele é uma lista de critérios tipados, e serializá-la na barra de
// endereço criaria uma segunda porta de entrada para filtro — sem a
// revalidação contra a allow-list que a aba faz. O que a URL carrega é só a
// INTENÇÃO.
export const ACAO_APLICAR_RECORTE = 'aplicar-recorte'
/**
 * Abrir a conversa de UM cliente no guia (17/09/2026) — o destino do "Abrir"
 * do cartão `fee-fixo/cliente-atalho-card` quando ele aparece FORA do módulo
 * (a Home IA). Quem o lê é `view-fee-fixo-guia-conversas.js::
 * abrirClienteDoDeepLink`, no `created` da view.
 *
 * ★ Aqui o ID vai junto na URL (`PARAM_CLIENTE`), ao contrário do recorte
 * acima — e não é incoerência: o cartão abre o guia em ABA NOVA (convenção de
 * sair do módulo de IA), e aba nova nasce com a store FRIA
 * (`utils/abrir-em-nova-aba.js`). Sem o id na URL, o destino chegaria vazio.
 * É o mesmo dado que a rota do Fee Lab já carrega em `:codigoCliente`.
 *
 * `nome` e `assunto` NÃO viajam: o nome é derivável na chegada
 * (`identidadeDaConversa`) e nome de cliente na barra de endereço entra no
 * histórico do navegador; `assunto` só semeia o compositor de dentro da aba.
 */
export const ACAO_ABRIR_CONVERSA_CLIENTE = 'abrir-conversa-cliente'
export const PARAM_CLIENTE = 'cliente'

// ── Alvos que o cartão emite em `cta` ──────────────────────────────────────
// Um vocabulário só: no modo de adoção o alvo é a chave do passo que o backend
// escolheu; no estado vazio, a ação de importação. Quem recebe (`view-fee-fixo-
// guia.vue`) resolve o destino pelo mapa abaixo, sem `if` por tela.
export const ALVO_IMPORTAR_CLIENTES = ACAO_IMPORTAR_CLIENTES
export const ALVO_IMPORTAR_ATIVOS = ACAO_IMPORTAR_ATIVOS

// ── Chaves do motor (espelho do backend) ───────────────────────────────────
export const PASSO_RODE_A_CONTA = 'rode_a_conta'
export const PASSO_MONTE_AS_REGUAS = 'monte_as_reguas'
export const PASSO_ESCOLHA_A_PRIMEIRA_ONDA = 'escolha_a_primeira_onda'
export const PASSO_AGENDE_AS_R1 = 'agende_as_r1'

export const SITUACAO_FEITO = 'feito'
export const SITUACAO_PENDENTE = 'pendente'
export const SITUACAO_NAO_OBSERVAVEL = 'nao_observavel'

export const ETAPA_FUNIL_BASE = 'base'
export const ETAPA_FUNIL_SIMULADOS = 'simulados'
export const ETAPA_FUNIL_EM_MIGRACAO = 'em_migracao'
export const ETAPA_FUNIL_FECHADOS = 'fechados'

// ── Rótulos ────────────────────────────────────────────────────────────────
export const ROTULO_DA_SITUACAO = {
  [SITUACAO_FEITO]: 'Feito',
  [SITUACAO_PENDENTE]: 'Pendente',
  // Nem "feito" nem "pendente": a plataforma não observa este passo, e o selo
  // não pode dar veredito no lugar do consultor (I-A3 aplicada ao rótulo).
  [SITUACAO_NAO_OBSERVAVEL]: 'A confirmar',
}

// `preserveCase` no BaseStatus depende destes rótulos virem já capitalizados —
// sem ele o componente derruba tudo para minúsculas.
export const COR_DA_SITUACAO = {
  // Tons de TEXTO das cores de status (`src/design/_controls.sass`): o selo em
  // modo `text` pinta a letra, e o verde/laranja cheios não têm contraste
  // suficiente sobre o branco do cartão.
  [SITUACAO_FEITO]: 'success-text',
  [SITUACAO_PENDENTE]: 'warning-text',
  [SITUACAO_NAO_OBSERVAVEL]: 'neutral-600',
}

export const ROTULO_DA_ETAPA_DO_METODO = {
  base_sem_clientes: 'Base ainda não subiu',
  antes_da_conta: 'Antes da conta',
  conta_rodada: 'Conta rodada',
  primeiras_conversas: 'Primeiras conversas',
  em_escala: 'Em escala',
  base_migrada: 'Base migrada',
}

export const ROTULO_DA_ETAPA_DO_FUNIL = {
  [ETAPA_FUNIL_BASE]: 'Base na plataforma',
  [ETAPA_FUNIL_SIMULADOS]: 'Com a conta rodada',
  [ETAPA_FUNIL_EM_MIGRACAO]: 'Na conversa de migração',
  [ETAPA_FUNIL_FECHADOS]: 'Já no fee fixo',
}

// Texto do botão por alvo. Alvo ausente daqui NÃO ganha botão: é assim que os
// passos que a plataforma não resolve (`monte_as_reguas`, `agende_as_r1`)
// deixam de oferecer um caminho que não existe.
export const CTA_DO_ALVO = {
  [PASSO_RODE_A_CONTA]: 'Rodar a conta no Fee Lab',
  [PASSO_ESCOLHA_A_PRIMEIRA_ONDA]: 'Escolher os clientes da primeira onda',
  [ALVO_IMPORTAR_CLIENTES]: 'Importar meus clientes',
  [ALVO_IMPORTAR_ATIVOS]: 'Importar a carteira',
}

// Para onde cada alvo leva. O `?acao=` é o que faz a tela de Clientes abrir o
// modal certo — sem ele o consultor chega na listagem e a instrução termina em
// "procure o botão".
export const DESTINO_DO_ALVO = {
  [ALVO_IMPORTAR_CLIENTES]: {
    name: 'fee-fixo.clientes',
    query: { [PARAM_ACAO]: ACAO_IMPORTAR_CLIENTES },
  },
  [ALVO_IMPORTAR_ATIVOS]: {
    name: 'fee-fixo.clientes',
    query: { [PARAM_ACAO]: ACAO_IMPORTAR_ATIVOS },
  },
  [PASSO_RODE_A_CONTA]: { name: 'fee-fixo.simulacao' },
  [PASSO_ESCOLHA_A_PRIMEIRA_ONDA]: { name: 'fee-fixo.clientes' },
}

/**
 * O destino de um alvo, ou `null` quando não há tela para onde mandar.
 *
 * Devolve uma CÓPIA: o chamador entrega o objeto ao `router.push`, que o
 * normaliza, e um destino compartilhado voltaria mutado para o próximo clique.
 *
 * @param {string=} alvo Valor emitido em `cta` pelo cartão de estado.
 * @returns {?Object} Location do vue-router, ou `null`.
 */
export const destinoDoAlvo = (alvo) => {
  const destino = DESTINO_DO_ALVO[alvo]
  if (!destino) return null
  return destino.query
    ? { name: destino.name, query: { ...destino.query } }
    : { name: destino.name }
}

/**
 * Rótulo do botão de um alvo, ou string vazia quando o alvo não tem ação de
 * plataforma. Vazio é a resposta certa: o cartão usa isso para NÃO desenhar o
 * botão.
 *
 * @param {string=} alvo
 * @returns {string}
 */
export const ctaDoAlvo = (alvo) => CTA_DO_ALVO[alvo] || ''

// ── Textos do cartão ───────────────────────────────────────────────────────
export const TITULO_DO_CARTAO = 'Onde a sua migração está'
export const ROTULO_PROXIMO_PASSO = 'Próximo passo'

export const TITULO_ERRO = 'Não consegui ler o estado da sua carteira agora.'
export const TEXTO_ERRO =
  'Nada mudou na sua base — só nesta leitura. Tente de novo em instantes; a conversa aqui do lado continua funcionando.'
export const ROTULO_TENTAR_DE_NOVO = 'Tentar de novo'

export const TITULO_VAZIO = 'Comece pela base'
export const TEXTO_VAZIO =
  'Ainda não há cliente na sua base. Suba a carteira pelo caminho que combina com a sua origem — depois disso o guia passa a acompanhar cada passo.'

export const CAMINHOS_DE_IMPORTACAO = [
  {
    alvo: ALVO_IMPORTAR_CLIENTES,
    titulo: 'Positivador',
    descricao:
      'A planilha de clientes da corretora: nome, código e patrimônio de cada um. É por aqui que a base nasce.',
  },
  {
    alvo: ALVO_IMPORTAR_ATIVOS,
    titulo: 'Diversificador',
    descricao:
      'A carteira real, ativo a ativo. É a alocação que a simulação usa no lugar da carteira modelo.',
  },
]

export const TITULO_COBERTURA = 'Quanto da carteira já passou'
export const TEXTO_COBERTURA =
  'Nenhum passo do método está pendente. A leitura agora é de cobertura: quantos clientes já percorreram cada etapa.'
export const ROTULO_GARGALO = 'Maior perda'

/**
 * Nome legível da etapa do método; o valor cru quando a etapa for
 * desconhecida — inventar "—" esconderia uma etapa nova do backend.
 *
 * @param {string=} etapa
 * @returns {string}
 */
export const rotuloDaEtapaDoMetodo = (etapa) =>
  ROTULO_DA_ETAPA_DO_METODO[etapa] || etapa || ''

/**
 * O recorte sugerido tem card na tela? ([G-R7])
 *
 * Mora aqui porque DOIS lugares precisam da mesma resposta: o card, que decide
 * se desenha, e a view, que conta o card como conversa começada (`semMensagens`
 * — a mesma regra dos anexos: card aberto já é conversa). Duas cópias da
 * condição divergiriam no primeiro ajuste, e o sintoma seria a saudação do v0
 * ao lado de um recorte, ou o inverso.
 *
 * Filtro RECUSADO pela allow-list não desenha nada — quem explica a recusa é o
 * card da aba Clientes, que tem o vocabulário dos motivos.
 *
 * @param {{valido: boolean, filtros: Array}=} filtro
 * @returns {boolean}
 */
export const temRecorteParaMostrar = (filtro) => {
  if (!filtro || !filtro.valido) return false
  return Array.isArray(filtro.filtros) && filtro.filtros.length > 0
}

// --- Cartão de atalho para a conversa de um cliente (spec-006, [T007]) ---
//
// Os rótulos moram aqui, e não no template, pela razão de sempre neste módulo:
// o texto que o consultor lê é vocabulário do produto, e vocabulário duplicado
// diverge no primeiro ajuste — foi assim que a entrada do guia acabou com duas
// promessas diferentes sobre a mesma lista (VERIFY da spec-003).

/** Título do cartão — ele anuncia a ação, não o dado. */
export const TITULO_ATALHO_DE_CLIENTE = 'Continuar com um cliente'

/**
 * O CTA de cada linha. Seta TEXTUAL de propósito: a paleta de glifos do guia
 * foi normalizada em `#8E8E8E` na spec-005, e um ícone novo aqui reabriria
 * aquele gate por um enfeite.
 */
export const CTA_ATALHO_DE_CLIENTE = 'Abrir →'

/**
 * Teto de linhas do cartão.
 *
 * ★ Desceu de 5 para 3 em 24/08/2026, POR MEDIÇÃO (§9.3 da spec-006): a linha
 * implementada mede ~60px — o CTA é `BaseButton underlined` (36px) no lugar do
 * `BaseStatus` (~20px) do precedente. Cinco linhas dariam ~300px dentro de uma
 * bolha; três dão ~180px, que é a altura do próprio `estado-card`, e o cartão
 * volta a ser proporcional à resposta. O botão NÃO foi trocado por texto
 * clicável: link de texto dentro de resposta de IA é justamente o controle que
 * ninguém vê, e o DS prescreve `BaseButton` para ação secundária.
 *
 * Quem cita mais de três clientes está pedindo o panorama — e o panorama é a
 * tabela na bolha, não uma lista de atalhos.
 */
export const TETO_DE_ATALHOS_DE_CLIENTE = 3

// Cartão "clientes citados" do chat (`ia/cartoes/cartao-clientes-citados.vue`).
export const PREFIXO_CODIGO_DO_CLIENTE = 'Código '
export const UNIDADE_CLIENTES_CITADOS = ' clientes'

/** @example subtituloClientesCitados(2) // '2 clientes citados' */
export const subtituloClientesCitados = (total) =>
  total === 1 ? '1 cliente citado' : total + ' clientes citados'

/**
 * O rodapé que DIZ quantos ficaram de fora.
 *
 * Mesma razão do corte do panorama (§7.1): três linhas escondendo oito parecem
 * a lista inteira. Truncar em silêncio faz o cartão mentir por omissão.
 *
 * @param {number} quantos Quantos clientes citados não couberam.
 * @returns {string} A frase, ou `''` quando coube tudo.
 */
export const rotuloDosQueFicaramDeFora = (quantos) => {
  const fora = Math.trunc(Number(quantos) || 0)
  if (fora <= 0) return ''
  const verbo = pluralize(fora, 'ficou', 'ficaram')
  const substantivo = pluralize(fora, 'cliente', 'clientes')
  return `Mais ${fora} ${substantivo} ${verbo} de fora`
}

// --- Cartão da simulação no thread (spec-007, [T107]) ---

/** Rótulo do cartão. O cliente é dito pela FRASE do backend, não montado aqui. */
// O cabeçalho da lista de premissas do cartão (G-2 do design-conformance).
// Sem ele o bloco que a US-3 torna obrigatório era um parágrafo cinza solto —
// o item de menor peso visual do cartão, entre um gráfico e dois botões.
export const TITULO_PREMISSAS_DA_SIMULACAO = 'Premissas do cenário'

/**
 * O consultor trocou de conversa enquanto o depósito estava em voo.
 *
 * ★ Decisão do lead (24/08/2026), caso 3 do MUST da review. As três saídas
 * possíveis terminam com o cenário DEPOSITADO; o que muda é onde fica a
 * incoerência. Esta termina em estado honesto e explicável: **nada foi
 * commitado**, e refazer custa um pedido — o depósito é idempotente e o
 * registro vive 30 min, então pedir de novo reaproveita o MESMO `preview_id`.
 *
 * A alternativa (mandar a frase para a conversa nova) grava certo, mas **num
 * lugar onde o consultor não está olhando** — o oposto do que o two-step
 * inteiro existe para garantir: nenhuma escrita sem alguém ver.
 *
 * A frase diz o que aconteceu E a próxima ação, que é a régua da casa para
 * mensagem de erro.
 */
export const MSG_SIMULACAO_TROCOU_DE_CONVERSA =
  'Você trocou de conversa enquanto eu salvava — nada foi gravado. Peça de ' +
  'novo na conversa do cliente.'

/**
 * O preview não foi registrado no servidor (SHOULD da review, 24/08/2026).
 *
 * O cartão já escondia o Salvar sem `previewId` — mas em SILÊNCIO: o consultor
 * via a conta inteira e um botão a menos, sem nada dizendo por quê. O irmão
 * (`fee-fixo-guia-confirmacao`) tem o par desde o G-11, e faltava aqui.
 *
 * Texto PRÓPRIO, e não o `aviso` do envelope: aquele vem redigido para o
 * MODELO, e despejá-lo na tela mostraria instrução de prompt a quem não é o
 * destinatário dela. Mesma decisão, mesma razão do irmão.
 */
export const AVISO_SIMULACAO_SEM_REGISTRO =
  'Não consegui registrar este preview. Peça a simulação de novo — salvar ' +
  'agora seria recusado.'

export const TITULO_SIMULACAO_DO_CHAT = 'Simulação'

/** O CTA que grava — e ele nomeia o destino, não a ação abstrata. */
export const CTA_SALVAR_SIMULACAO = 'Salvar no Fee Lab'

/** A saída que sobra quando o Salvar não é oferecido. */
export const CTA_CANCELAR_SIMULACAO = 'Cancelar'

/**
 * T037 (G-7, QA it.3, spec-016): o depósito de `resultados` (contrato
 * `deposito-de-resultados.md`) agora acontece SOZINHO ao renderizar o
 * preview — não só no clique de Salvar —, porque a confirmação NATURAL
 * (dizer "sim, pode gravar" no chat) pula o clique inteiro. Erro aqui é
 * SILENCIOSO por natureza (ninguém clicou em nada para ver o resultado), e
 * por isso precisa de texto e ação PRÓPRIOS — nunca reusar `MSG_ERRO_DEPOSITO`
 * de `apiFeeFixoSimulacaoDeposito.js` (aquela fala de "não confirme ainda",
 * que pressupõe um clique que aqui não existe).
 */
export const MSG_ERRO_DEPOSITO_AUTOMATICO =
  'Não consegui guardar os números desta simulação.'

/**
 * A recusa LÓGICA do depósito: o `previewId` pedido não é o preview corrente
 * da store (um ajuste o substituiu, ou a pendência já caiu). Não é falha de
 * rede — a frase acima diria ao consultor para tentar de novo o que o
 * servidor nunca vai aceitar.
 */
export const MSG_PREVIEW_NAO_CORRENTE =
  'Esta simulação não é mais a corrente. Rode a conta de novo.'

/** O CTA do retry — nomeia a AÇÃO, não o efeito, como o irmão `CTA_SALVAR_*`. */
export const CTA_TENTAR_DEPOSITO_DE_NOVO = 'Tentar de novo'

/**
 * A frase da guarda de plausibilidade.
 *
 * Ela diz o que houve e o que fazer — sem número, porque é exatamente o número
 * que não fechou. Publicar os bps "que estão certos" ao lado dela faria o
 * cenário parecer quase inteiro, quando o que falta é o que vai ao cliente.
 */
export const MSG_CALCULO_NAO_FECHOU =
  'Não consegui fechar esta simulação — faltou premissa para calcular os ' +
  'valores em reais. Peça de novo ao especialista; se persistir, simule pelo ' +
  'Fee Lab.'

/**
 * O cartão do cenário JÁ GRAVADO — o DESFECHO de `criar_simulacao_fee_fixo`
 * (G-14, 05/09/2026).
 *
 * Copy no PASSADO e sem verbo de decisão: o consultor já confirmou, e um
 * "Confirmar"/"Salvar" aqui ofereceria de novo o que acabou de acontecer. O
 * que resta é ver o que foi salvo — daí um CTA só, de leitura.
 */
export const TITULO_CENARIO_GRAVADO = 'Cenário salvo no Fee Lab'

/**
 * ★ [owner, 19/09/2026] A seta textual SAIU. Ela existia porque o cartão não
 * tinha moldura e o rótulo precisava se anunciar como ação; hoje ele é a
 * `CartaoCasca`, e o CTA mora no cabeçalho dela — onde o affordance é do
 * próprio botão. Duas setas no mesmo lugar é uma a mais.
 */
export const CTA_CENARIO_GRAVADO = 'Abrir no Fee Lab'

/**
 * O aviso de `cenario_completo: false`.
 *
 * Nunca aparece no caminho de hoje (a completude é invariante do depósito —
 * `[T113]`), e existe justamente por isso: se um dia um cenário chegar
 * incompleto, o consultor precisa ler POR QUE o relatório não sai, em vez de
 * clicar num caminho mudo.
 */
export const AVISO_CENARIO_INCOMPLETO =
  'O cenário foi salvo sem os números calculados — o relatório e a apresentação ' +
  'só ficam disponíveis depois de rodar a conta de novo na tela.'
